// @owners { team: patients-team }
import { Button, Column, InputSelect, InputText, MdSpacing, Toast, ToastContext } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useSignPatientAuthorization } from '~web/features/info-request/hooks';

type Props = {
  readonly buttonLabel: string;
  readonly hubReferralID: number;
  readonly onClose: () => void;
  readonly nextURL?: string;
};

type PatientAuthorizationValues = {
  signerRelationship: string;
  fullName: string;
};

const signerRelationshipOptions = [
  { value: 'self', label: 'Self' },
  { value: 'spouse', label: 'Spouse/partner' },
  { value: 'parent', label: 'Parent' },
  { value: 'other', label: 'Other' },
];

export const PatientAuthorizationForm = ({ buttonLabel, hubReferralID, onClose, nextURL }: Props) => {
  const { navigate } = useNavigation();
  const { trackEvent } = useAnalytics();
  const { addToast } = useContext(ToastContext);
  const { mutateAsync: signPatientAuthorization } = useSignPatientAuthorization();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    clearErrors,
    setValue,
  } = useForm<PatientAuthorizationValues>({
    defaultValues: {
      signerRelationship: 'self',
    },
  });

  const onSubmit = async (formValues: PatientAuthorizationValues) => {
    trackEvent({
      event: EVENTS.INFO_REQUEST__SUBMIT_ELECTRONIC_SIGNATURE_CLICKED,
      params: { hub_referral_id: hubReferralID, next_url: nextURL },
    });

    const params = {
      signer_relationship: formValues.signerRelationship,
      hub_referral_id: hubReferralID,
      signer_full_name: formValues.fullName,
    };

    const response = await signPatientAuthorization(params);

    if (response.errors) {
      addToast(<Toast variant="error">Failed to sign the patient authorization. Please try again.</Toast>);
      trackEvent({
        event: EVENTS.INFO_REQUEST__SUBMIT_ELECTRONIC_SIGNATURE_FAILED,
        params: { hub_referral_id: hubReferralID, next_url: nextURL },
      });
    } else {
      addToast(<Toast variant="success">Thanks for signing your patient authorization form.</Toast>);
      trackEvent({
        event: EVENTS.INFO_REQUEST__SUBMIT_ELECTRONIC_SIGNATURE_SUCCEED,
        params: { hub_referral_id: hubReferralID, next_url: nextURL },
      });
      if (nextURL) {
        navigate(nextURL);
      }
    }
    reset();
    onClose();
  };

  return (
    <Column>
      <InputSelect
        label="Relationship to Patient"
        {...register('signerRelationship')}
        defaultValue="self"
        onValueChange={(value) => {
          setValue('signerRelationship', value);
          clearErrors('signerRelationship');
        }}
        options={signerRelationshipOptions}
        testID="signer-relationship-select"
      />
      <MdSpacing />
      <InputText
        label="Full Name"
        {...register('fullName', {
          required: 'Full name is required',
        })}
        onChangeText={(value) => {
          setValue('fullName', value);
          clearErrors('fullName');
        }}
        error={errors.fullName?.message}
        required
        accessibilityLabel="full-name-input"
        testID="full-name-input"
        caption="By typing and submitting your name, you are agreeing to this as the electronic representation of your signature on this document."
      />
      <MdSpacing />
      <Button
        testID="sign-patient-authorization-button"
        label={buttonLabel}
        loading={isSubmitting}
        onPress={handleSubmit(onSubmit)}
      />
    </Column>
  );
};
